import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, Check, X, Truck, Clock, ChefHat, Package, CheckCircle2,
  Pencil, MapPin, Copy, CalendarClock, RefreshCw, FileText, Navigation,
  EyeOff, Ban, MoreVertical, Link, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const STATUS_FLOW = ["pending", "preparing", "ready", "delivering", "delivered"];
const STATUS_TIMELINE = [
  { key: "pending", label: "Recebido", icon: Clock },
  { key: "preparing", label: "Produzindo", icon: ChefHat },
  { key: "ready", label: "Pronto", icon: Package },
  { key: "delivering", label: "Enviado", icon: Truck },
  { key: "delivered", label: "Entregue", icon: CheckCircle2 },
];

export function CashChangeCalculator({ order, formatPrice }) {
  const [received, setReceived] = useState("");
  const isCash = ["cash", "efectivo", "dinheiro"].some(m => (order.payment_method || "").toLowerCase().includes(m));
  if (!isCash) return null;

  const total = order.total || 0;
  const receivedNum = parseFloat(received) || 0;
  const change = receivedNum > total ? receivedNum - total : 0;

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid="cash-change-section">
      <p className="text-[10px] font-bold text-green-700 uppercase">Troco / Cambio</p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 whitespace-nowrap">Recebido:</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={received}
          onChange={e => setReceived(e.target.value)}
          placeholder={total.toFixed(2)}
          className="h-8 text-sm font-bold bg-white w-24"
          data-testid="cash-received-input"
        />
        <span className="text-xs text-gray-400">EUR</span>
      </div>
      {receivedNum > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Troco:</span>
          <span className={`text-lg font-black ${change > 0 ? "text-green-700" : "text-gray-400"}`} data-testid="cash-change-amount">
            {formatPrice(change)}
          </span>
        </div>
      )}
    </div>
  );
}

export function CustomerNifSection({ order, authAxios, onUpdate }) {
  const [nif, setNif] = useState(order.customer_nif || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/orders/${order.id}`, { customer_nif: nif.trim() });
      setSaved(true);
      toast.success("NIF guardado");
      setTimeout(() => setSaved(false), 2000);
      if (onUpdate) onUpdate();
    } catch {
      toast.error("Error al guardar NIF");
    }
    setSaving(false);
  };

  return (
    <div data-testid="customer-nif-section">
      <h3 className="font-bold text-xs text-gray-500 uppercase tracking-wider mb-2">NIF / DNI</h3>
      <div className="flex items-center gap-2">
        <Input
          value={nif}
          onChange={e => { setNif(e.target.value); setSaved(false); }}
          placeholder="NIF do cliente"
          className="h-8 text-sm flex-1"
          data-testid="customer-nif-input"
        />
        <Button
          size="sm"
          variant={saved ? "default" : "outline"}
          className={`h-8 text-xs ${saved ? "bg-emerald-600" : ""}`}
          onClick={handleSave}
          disabled={saving || !nif.trim()}
          data-testid="save-nif-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

export function StatusTimeline({ order }) {
  const history = order.status_history || [];
  const historyMap = {};
  for (const h of history) { historyMap[h.status] = h.timestamp; }
  if (!historyMap["pending"] && order.created_at) historyMap["pending"] = order.created_at;

  const currentIdx = STATUS_FLOW.indexOf(order.status);
  const isCancelled = order.status === "cancelled";
  const N = STATUS_TIMELINE.length;
  const progressPct = isCancelled ? 0 : Math.max(0, currentIdx) / (N - 1) * 100;

  const formatTime = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="w-full" data-testid="status-timeline">
      <div className="relative flex justify-between items-center">
        <div className="absolute top-1/2 -translate-y-1/2 left-3.5 right-3.5 h-0.5 bg-gray-200" />
        {!isCancelled && currentIdx > 0 && (
          <div
            className="absolute top-1/2 -translate-y-1/2 left-3.5 h-0.5 bg-emerald-500 transition-all duration-500"
            style={{ width: `calc((100% - 28px) * ${Math.min(progressPct, 100)} / 100)` }}
          />
        )}
        {STATUS_TIMELINE.map((step, idx) => {
          const isDone = !isCancelled && currentIdx >= idx;
          const isCurrent = !isCancelled && order.status === step.key;
          const Icon = step.icon;
          return (
            <div
              key={step.key}
              className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                isCurrent
                  ? "bg-emerald-600 text-white ring-2 ring-emerald-300 ring-offset-1 scale-110"
                  : isDone
                  ? "bg-emerald-500 text-white"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5">
        {STATUS_TIMELINE.map((step, idx) => {
          const isDone = !isCancelled && currentIdx >= idx;
          const isCurrent = !isCancelled && order.status === step.key;
          const timestamp = historyMap[step.key];
          return (
            <div
              key={step.key}
              className={`flex flex-col items-center w-7 ${idx === 0 ? "items-start" : idx === N - 1 ? "items-end" : "items-center"}`}
            >
              <span className={`text-[9px] font-semibold leading-tight ${isCurrent ? "text-emerald-700" : isDone ? "text-emerald-600" : "text-gray-400"}`}>
                {step.label}
              </span>
              {timestamp && isDone && (
                <span className="text-[9px] text-gray-400 leading-tight">{formatTime(timestamp)}</span>
              )}
            </div>
          );
        })}
      </div>
      {isCancelled && (
        <div className="flex items-center gap-2 mt-2">
          <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
            <X className="w-3 h-3" />
          </div>
          <span className="text-[10px] font-semibold text-red-600">Cancelado</span>
        </div>
      )}
    </div>
  );
}

export function OrderActionsMenu({ order, canEditDetails, isDelivery, isFinished,
  cancelling, openEditDialog, handleDuplicate, handlePrint, handleCreateInvoice,
  handleFinalize, setShowCancelDialog, setCancelReason, setShowPaymentDialog,
  setShowScheduleDialog, setScheduleFor }) {
  const [open, setOpen] = useState(false);

  const action = (fn) => { fn(); setOpen(false); };

  const hasCoords = isDelivery && order.delivery_lat && order.delivery_lng;
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${order.delivery_lat},${order.delivery_lng}&travelmode=driving`
    : null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid="order-actions-menu-btn">
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="order-actions-dropdown">
        <DropdownMenuLabel className="text-[10px] text-gray-400 uppercase tracking-wider px-3 py-1">Edição</DropdownMenuLabel>
        {canEditDetails && (
          <DropdownMenuItem onClick={() => action(openEditDialog)} className="gap-2 cursor-pointer" data-testid="action-edit">
            <Pencil className="w-3.5 h-3.5 text-blue-500" />Editar pedido
          </DropdownMenuItem>
        )}
        {isDelivery && order.status !== "cancelled" && (
          <DropdownMenuItem onClick={() => action(openEditDialog)} className="gap-2 cursor-pointer" data-testid="action-edit-address">
            <MapPin className="w-3.5 h-3.5 text-teal-500" />Editar morada
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] text-gray-400 uppercase tracking-wider px-3 py-1">Operações</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => action(handleDuplicate)} className="gap-2 cursor-pointer" data-testid="action-duplicate">
          <Copy className="w-3.5 h-3.5 text-purple-500" />Duplicar pedido
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => action(() => navigator.clipboard.writeText(`${window.location.origin}/orders?id=${order.id}`).then(() => toast.success("Link interno copiado")).catch(() => toast.error("Erro ao copiar link")))}
          className="gap-2 cursor-pointer"
          data-testid="action-copy-link-internal"
        >
          <Link className="w-3.5 h-3.5 text-sky-500" />Copiar link interno
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => action(() => navigator.clipboard.writeText(`${window.location.origin}/track/${order.id}`).then(() => toast.success("Link de acompanhamento copiado")).catch(() => toast.error("Erro ao copiar link")))}
          className="gap-2 cursor-pointer"
          data-testid="action-copy-link-tracking"
        >
          <ExternalLink className="w-3.5 h-3.5 text-teal-500" />Copiar link de acompanhamento
        </DropdownMenuItem>
        {order.status !== "cancelled" && (
          <DropdownMenuItem onClick={() => action(() => { setScheduleFor(order.scheduled_for ? order.scheduled_for.substring(0, 16) : ""); setShowScheduleDialog(true); })} className="gap-2 cursor-pointer" data-testid="action-schedule">
            <CalendarClock className="w-3.5 h-3.5 text-orange-500" />{order.scheduled_for ? "Reagendar pedido" : "Agendar pedido"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => action(() => setShowPaymentDialog(true))} className="gap-2 cursor-pointer" data-testid="action-payment">
          <RefreshCw className="w-3.5 h-3.5 text-emerald-500" />Alterar pagamento
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => action(handleCreateInvoice)} className="gap-2 cursor-pointer" data-testid="action-invoice">
          <FileText className="w-3.5 h-3.5 text-blue-500" />
          {order.vendus_invoice_number ? "Ver factura" : "Emitir factura"}
        </DropdownMenuItem>
        {mapsUrl && (
          <DropdownMenuItem asChild className="gap-2 cursor-pointer" data-testid="action-maps">
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <Navigation className="w-3.5 h-3.5 text-teal-500" />Ver no Maps
            </a>
          </DropdownMenuItem>
        )}
        {isFinished && !order.hidden && order.status !== "cancelled" && (
          <DropdownMenuItem onClick={() => action(handleFinalize)} className="gap-2 cursor-pointer" data-testid="action-finalize">
            <EyeOff className="w-3.5 h-3.5 text-gray-500" />Finalizar pedido
          </DropdownMenuItem>
        )}
        {order.status !== "cancelled" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] text-gray-400 uppercase tracking-wider px-3 py-1">Crítico</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => action(() => { setCancelReason(""); setShowCancelDialog(true); })}
              className="gap-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
              disabled={cancelling}
              data-testid="action-cancel"
            >
              {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
              Cancelar pedido
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
